import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaCrown, FaPlus, FaBook } from 'react-icons/fa';

const API = '/api';

const roleOptions = [
  { title: 'Vision Keeper', description: 'Sets the long-term direction and goals for the family' },
  { title: 'Scribe', description: 'Documents family history, stories, and important decisions' },
  { title: 'Harvest Guardian', description: 'Manages family finances and resource allocation' },
  { title: 'Bridge Builder', description: 'Maintains connections and resolves conflicts between family members' },
  { title: 'Legacy Anchor', description: 'Preserves traditions and passes down cultural heritage' },
  { title: 'Circle Steward', description: 'Organizes gatherings and ensures inclusive participation' },
  { title: 'Archivist', description: 'Maintains photos, documents, and family memorabilia' }
];

export function RolesTab({ roles, familyMembers, familyId, showForm, setShowForm, onRoleCreated }) {
  const [formData, setFormData] = useState({
    user_id: '',
    title: '',
    description: ''
  });
  const [creating, setCreating] = useState(false);

  // Get description for a role title
  const getRoleDescription = (title) => {
    const role = roleOptions.find(r => r.title === title);
    return role ? role.description : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      // Auto-fill description based on selected role if not custom
      const roleDescription = formData.description || getRoleDescription(formData.title);
      
      const response = await axios.post(
        `${API}/roles`,
        { ...formData, description: roleDescription, family_id: familyId },
        { withCredentials: true }
      );
      
      onRoleCreated(response.data);
      setShowForm(false);
      setFormData({ user_id: '', title: '', description: '' });
    } catch (error) {
      console.error('Error creating role:', error);
      alert('Failed to assign role');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div data-testid="roles-tab" className="space-y-6">
      {/* Actions Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Role Actions</h4>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            data-testid="assign-role-btn"
          >
            <FaPlus />
            <span>Assign Role</span>
          </button>
          <Link
            to="/role-guide"
            className="flex items-center space-x-2 px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 font-semibold"
            data-testid="role-guide-link"
          >
            <FaBook />
            <span>Role Guide</span>
          </Link>
        </div>
      </div>

      {/* Assigned Roles Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Assigned Roles</h4>
        {roles.length === 0 ? (
          <div className="text-center py-8">
            <FaCrown className="text-gray-300 text-6xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No roles assigned yet</h3>
            <p className="text-gray-600 mb-4">Start assigning roles to organize your family leadership!</p>
            <Link 
              to="/role-guide" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Learn about family roles →
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map((role) => {
              const member = familyMembers.find(m => m.user_id === role.user_id);
              const defaultDesc = getRoleDescription(role.title);
              return (
                <div
                  key={role.role_id}
                  className="bg-gray-50 rounded-xl border border-gray-200 hover:border-yellow-300 transition p-6"
                  data-testid={`role-card-${role.role_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <FaCrown className="text-yellow-500 text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{role.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">{member?.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500 italic">
                    {role.description || defaultDesc}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assign Role Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="assign-role-modal">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Assign Role</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Family Member</label>
                <select
                  value={formData.user_id}
                  onChange={(e) => setFormData({ ...formData, user_id: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="role-member-select"
                >
                  <option value="">Select a member</option>
                  {familyMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Role Title</label>
                <select
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  required
                  data-testid="role-title-select"
                >
                  <option value="">Select a role</option>
                  {roleOptions.map((role) => (
                    <option key={role.title} value={role.title}>
                      {role.title} ({role.description})
                    </option>
                  ))}
                </select>
              </div>

              {/* Show selected role description */}
              {formData.title && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">{formData.title}:</span> {getRoleDescription(formData.title)}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-gray-700 font-medium mb-2">Custom Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  rows="3"
                  placeholder="Leave empty to use the default description above"
                  data-testid="role-description-input"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  data-testid="submit-role-btn"
                >
                  {creating ? 'Assigning...' : 'Assign Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
